<style scoped>
ul {
    list-style-type: none;
    padding-left: 5px;
}
.icon-download:before {
    text-decoration: none;
    margin-right: 5px;
}
.editor-help-text {
    cursor: default;
    margin-bottom: 5px;
}
.file-header {
    font-size: 1.2em;
    font-weight: 600;
}
.status-icon {
    position: absolute;
    right: 30px;
    top: 50%;
    transform: translateY(-50%);
    -webkit-transform: translateY(-50%);
    -ms-transform: translateY(-50%);
}
.field-validation-error .icon.icon-attention {
    color: inherit;
}
</style>
<template>
    <div>
        <template v-if="viewTypes.length > 0">
            <h3 class="file-header">Generated Files</h3>
            <ul>
                <template v-for="viewType in viewTypes">
                    <Promised :pending-delay="null" :promise="getViewTypeUrl(viewType)" v-slot="url">
                        <li>
                            <a class="icon icon-download" :href="url" target="_blank">{{ viewType == "Builtin" || viewType == "Default" ? "Submission" : viewType }}</a>
                        </li>
                    </Promised>
                </template>
            </ul>
        </template>
        <template v-if="allFileElementsWithFile != null && allFileElementsWithFile.length > 0">
            <h3 class="file-header">Form Attachments</h3>
            <ul>
                <li v-for="fileElement in allFileElementsWithFile">
                    <a class="icon icon-download" target="_blank" :href="fileElement.fileUrl">{{ fileElement.fileName }}</a>
                    <span class="editor-help-text">{{ fileElement.definition.settings.label || fileElement.definition.settings.shortLabel }}</span>
                </li>
            </ul>
        </template>
        <h3 class="file-header">Reviewer Uploads</h3>
        <template v-if="allElementsWithFileComments.length > 0">
            <ul>
                <template v-for="element in allElementsWithFileComments">
                    <template v-for="comment in element.comments.filter(i => i.files != null && i.files.length > 0)">
                        <template v-for="file in comment.files">
                            <li>
                                <span>
                                    <a class="icon icon-download" target="_blank" :href="file.fileUrl">{{ file.filename }}</a>
                                    <button v-if="canDeleteFiles" class="passthrough icon icon-trash ml-3" title="Delete" aria-label="Delete" @click.prevent="deleteFile(element, comment, file)" />
                                </span>
                                <span class="editor-help-text">{{ elementLabel(element) }}</span>
                            </li>
                        </template>
                    </template>
                </template>
            </ul>
        </template>
        <div class="btn btn-file form-group p-0 m-0">
            <label for="FilesUpload" class="sr-only">Attach Files</label>
            <input type="file" id="FilesUpload" ref="file" multiple @change="uploadFiles" />
            <div class="status-icon">
                <AsyncOperationStatus :promise="uploadPromise" />
            </div>
        </div>
        <div v-if="anyThreat">
            <span class="field-validation-error"><i class="icon icon-attention align-middle mr-1" title="Error" role="img" />Potential virus or malware detected.</span>
        </div>
        <div aria-live="polite" class="sr-only">
            <template v-if="uploadStatus && uploadStatus != 'uploading'">
                <span v-if="anyThreat">Error: Potential virus or malware detected.</span>
                <span v-else>File uploaded successfully</span>
            </template>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { Promised } from "@/lib/Promised";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import { useObservable } from "@vueuse/rxjs";
import { map, switchMap } from "rxjs/operators";
import { combineLatest } from "rxjs";
import { useMainStore } from "@/mainStore";
import { usePageStore } from "../pageState/store";
import { toObserverComputed } from "../pageState/store";
import FileDef from "../elements/FileDef";
import FormDef from "../elements/FormDef";
//eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in html
import type { IControlSettings } from "@cs-ts/i-control-settings";
import { BaseElementDefBase } from "../elements/BaseElementDef";
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { getXsftHeader } from "@/lib/getXsrfToken.3";
import { FormComment } from "@cs-ts/form-comment";
import { asyncComputed } from "@vueuse/core";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
import { isNullOrWhitespace } from "@/lib/whitespaceMethods";
import { FormCommentFile } from "@cs-ts/form-comment-file";

export default defineComponent({
    components: {
        Promised,
        AsyncOperationStatus,
    },
    setup() {
        const uploadPromise = ref(null as null | Promise<boolean>);
        const uploadStatus = ref(null! as null | "uploading" | "failed" | "ok");
        const fileRef = ref(null! as HTMLInputElement);
        const anyThreat = ref(false);
        const mainStore = useMainStore();
        const pageStore = usePageStore();

        const allowDeleteFiles = asyncComputed(() => mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.DeleteCommentsAttachments.Id));
        const elementData = computed(() => pageStore.formDataTyped! as FormDef & { isThreat: boolean });
        const viewTypes = computed(() => pageStore.viewTypes);
        const allElementsWithFileComments = computed(() => pageStore.allElementsWithFileComments);
        const canDeleteFiles = computed(() => pageStore.adminReview && allowDeleteFiles.value && pageStore.canDeleteReviewComments);

        const allFileElementsWithFile = useObservable(
            toObserverComputed(() => pageStore.allFileElements as FileDef[]).pipe(
                //tap(i => { debugger; }),
                switchMap(k => combineLatest(k.map(i => i.hasValue$().pipe(map(j => [i, j] as const))))),
                //tap(i => { debugger; }),
                map(i => i.filter(i => i[1]).map(i => i[0])),
                //tap(i => {
                //    i[0].comments[0].files[0];
                //}),
            ),
        );

        const getViewTypeUrl = (viewType: string) => {
            return mainStore.getUrl(pageStore.adminReview ? "adminSearchReviewDocument" : "downloadDocument", {
                tenantCode: pageStore.formTenantCode,
                areaCode: pageStore.formTenantCode,
                formId: pageStore.formId,
                document: viewType,
            }) as Promise<string>;
        };
        const uploadFiles = () => {
            uploadStatus.value = "uploading";
            uploadPromise.value = (async () => {
                const elementId = pageStore.getElementPathString(elementData.value);
                try {
                    const htmlFiles = Array.from(fileRef.value.files as unknown as File[]);
                    const filesPromises = htmlFiles.map(async i => (await pageStore.uploadExternalFile({ vmId: elementData.value.vmId, newFile: i, isComment: true }))!);
                    const fileObjects = await Promise.all(filesPromises);
                    const fileIds = fileObjects.map(i => i.valueId);
                    const fileNames = htmlFiles.map(i => i.name);
                    const fileContentTypes = htmlFiles.map(i => i.type);
                    anyThreat.value = fileObjects.some(i => i.isThreat);
                    if (anyThreat.value) {
                        throw new Error("Potential virus or malware detected.");
                    }
                    /*const response = */ await pageStore.addCommentAndChangeStatus({ elementId, elementData: elementData.value, fileIds, fileNames, fileContentTypes, text: null!, status: null!, visibleToSubmitter: null!, userName: null! }); // TODO?
                    //if (response && !response.accessible) {
                    //    alert("As a result of your change, this submission is no longer accessible.");
                    //    this.$emit("change-page", { page: "AdminSearch" })
                    //    return;
                    //}
                    fileRef.value.value = null!; // clear attachments
                    uploadStatus.value = "ok";
                    return true;
                } catch (e) {
                    uploadStatus.value = "failed";
                    alert("Saving comment failed");
                    throw e;
                }
            })();
        };
        const deleteFile = async (element: BaseElementDefBase, comment: FormComment, file: FormCommentFile) => {
            if (!confirm("Delete file?")) {
                return;
            }
            const elementId = pageStore.getElementPathString(element);
            const _formData = {
                elementId,
                pageNumber: 1,
                formId: elementData.value.formId,
                commentId: comment.id,
                valueId: file.valueId,
            };
            const formData = new FormData();
            for (const key of Object.keys(_formData)) {
                //@ts-ignore
                formData.append(key, _formData[key]);
            }
            const deleteUrl = await mainStore.getUrl("deleteComment", { tenantCode: pageStore.formTenantCode, formId: pageStore.formId });
            const deleteResponse = await fetch(deleteUrl, {
                method: "POST",
                headers: await getXsftHeader(mainStore),
                credentials: "same-origin",
                body: formData,
            });
            throwIfNonSuccessStatus(deleteResponse);

            const remove = <T,>(arr: T[], func: (i: T) => boolean) => arr.splice(arr.findIndex(func), 1);
            remove(comment.files, i => file.valueId == i.valueId);
            if (isNullOrWhitespace(comment.text) && comment.files.length == 0) {
                remove(element.comments, i => comment.id == i.id);
            }
        };

        return {
            file: fileRef,
            viewTypes,
            canDeleteFiles,
            getViewTypeUrl,

            allFileElementsWithFile,
            allElementsWithFileComments,

            uploadFiles,
            deleteFile,
            uploadPromise,
            uploadStatus,
            elementData,
            anyThreat,

            elementLabel: (element: BaseElementDefBase) => (element.definition.settings as IControlSettings).label || (element.definition.settings as IControlSettings).shortLabel || "Unnamed Element",
        };
    },
});
</script>
